import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiClock,
  FiEye,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiUserCheck,
  FiUserPlus,
  FiXCircle,
} from "react-icons/fi";

import Footer from "../../../components/Footer/Footer";
import { LoadingButton } from "../../../components/loadingButtom";
import { ModalDadosPessoaisCliente } from "../../../components/modais/ModalDadosPessoaisCliente";
import { ModalDesconectar } from "../../../components/modais/ModalDesconectar";
import Navbar from "../../../components/Navbar/Navbar";
import { handleStatus } from "../../../components/Tabelas/TabelaGerenciarCadastro/functions";
import { retornarUsuariosEmEspera } from "./functions";
import "./GerenciarCadastro.css";

export interface Cliente {
  idCredenciais: string;
  nome: string;
  idsServicosContratados: string[];
}

type CadastroComServico = Cliente & {
  servico: "Assessoria" | "Private";
};

type CadastroActionsProps = {
  usuario: CadastroComServico;
  reload: boolean;
  setReload: React.Dispatch<React.SetStateAction<boolean>>;
  onDetalhes: (usuario: CadastroComServico) => void;
};

const normalizarTexto = (valor?: string) =>
  (valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const obterIniciais = (nome?: string) => {
  const partes = (nome || "Cliente")
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2);

  return partes.map((parte) => parte[0]).join("").toUpperCase() || "CL";
};

const possuiServicoPrivate = (usuario: Cliente) =>
  (usuario.idsServicosContratados || []).map(String).includes("3");

const formatarAtualizacao = (data?: Date) => {
  if (!data) {
    return "Aguardando atualização";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  }).format(data);
};

const CadastroActions = ({
  usuario,
  reload,
  setReload,
  onDetalhes,
}: CadastroActionsProps) => {
  const [loading, setLoading] = useState<boolean>(false);

  const aprovarCadastro = () =>
    handleStatus(usuario.idCredenciais, 1, setLoading, reload, setReload);

  return (
    <>
      <div className="gerenciar-cadastro-actions" aria-label={`Ações de ${usuario.nome}`}>
        <button
          type="button"
          aria-label={`Ver dados pessoais de ${usuario.nome}`}
          title="Dados pessoais"
          onClick={() => onDetalhes(usuario)}
          disabled={loading}
        >
          <FiEye />
        </button>
        <button
          type="button"
          className="approve"
          aria-label={`Aprovar cadastro de ${usuario.nome}`}
          title="Aprovar"
          onClick={aprovarCadastro}
          disabled={loading}
        >
          <FiCheckCircle />
        </button>
        <button
          type="button"
          className="reject"
          aria-label={`Reprovar cadastro de ${usuario.nome}`}
          title="Reprovar"
          onClick={() => handleStatus(usuario.idCredenciais, 0, setLoading, reload, setReload)}
          disabled={loading}
        >
          <FiXCircle />
        </button>
      </div>
    </>
  );
};

const GerenciarCadastro = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [modalDadosPessoais, setModalDadosPessoais] = useState<boolean>(false);
  const [erro, setErro] = useState<boolean>(false);
  const [desconectar, setDesconectar] = useState<boolean>(false);
  const [idCliente, setIdCliente] = useState<string>("");
  const [usuariosEmEspera, setUsuariosEmEspera] = useState<Array<Cliente>>([]);
  const [busca, setBusca] = useState<string>("");
  const [filtroServico, setFiltroServico] = useState<"Todos" | "Assessoria" | "Private">("Todos");
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState<Date>();

  useEffect(() => {
    let ativo = true;

    const carregarCadastros = async () => {
      await retornarUsuariosEmEspera(
        setLoading,
        setErro,
        setDesconectar,
        setUsuariosEmEspera
      );

      if (ativo) {
        setUltimaAtualizacao(new Date());
      }
    };

    carregarCadastros();

    return () => {
      ativo = false;
    };
  }, [reload]);

  const cadastros = useMemo<CadastroComServico[]>(
    () =>
      usuariosEmEspera.map((usuario) => ({
        ...usuario,
        servico: possuiServicoPrivate(usuario) ? "Private" : "Assessoria",
      })),
    [usuariosEmEspera]
  );

  const cadastrosFiltrados = useMemo(() => {
    const termo = normalizarTexto(busca);

    return cadastros.filter((usuario) => {
      const correspondeServico =
        filtroServico === "Todos" || usuario.servico === filtroServico;
      const correspondeBusca = !termo || normalizarTexto(usuario.nome).includes(termo);

      return correspondeServico && correspondeBusca;
    });
  }, [busca, cadastros, filtroServico]);

  const totalPrivate = cadastros.filter((usuario) => usuario.servico === "Private").length;
  const totalAssessoria = cadastros.length - totalPrivate;

  const abrirDadosPessoais = (usuario: CadastroComServico) => {
    setIdCliente(usuario.idCredenciais);
    setModalDadosPessoais(true);
  };

  const recarregar = () => setReload((valorAtual) => !valorAtual);

  const renderizarAcoes = (usuario: CadastroComServico) => (
    <CadastroActions
      usuario={usuario}
      reload={reload}
      setReload={setReload}
      onDetalhes={abrirDadosPessoais}
    />
  );

  return (
    <>
      <Navbar />

      <main className="gerenciar-cadastro-page">
        <section className="gerenciar-cadastro-hero">
          <div className="gerenciar-cadastro-hero-copy">
            <span>Onboarding</span>
            <h1>Gerenciar cadastros</h1>
            <p>
              Revise usuários em espera, consulte documentos e aprove o
              acesso exclusivamente com a conta master da Omex Capital.
            </p>
          </div>

          <div className="gerenciar-cadastro-metrics" aria-label="Resumo de cadastros">
            <div>
              <FiUserPlus />
              <span>Pendentes</span>
              <strong>{cadastros.length}</strong>
            </div>
            <div>
              <FiUserCheck />
              <span>Assessoria</span>
              <strong>{totalAssessoria}</strong>
            </div>
            <div>
              <FiShield />
              <span>Private</span>
              <strong>{totalPrivate}</strong>
            </div>
          </div>
        </section>

        <section className="gerenciar-cadastro-toolbar" aria-label="Filtros de cadastros">
          <label className="gerenciar-cadastro-search">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              value={busca}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setBusca(event.target.value)
              }
              placeholder="Buscar por nome"
            />
          </label>

          <div className="gerenciar-cadastro-filter" role="group" aria-label="Filtrar por serviço">
            {["Todos", "Assessoria", "Private"].map((filtro) => (
              <button
                key={filtro}
                type="button"
                className={filtroServico === filtro ? "active" : ""}
                onClick={() => setFiltroServico(filtro as "Todos" | "Assessoria" | "Private")}
              >
                {filtro}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="gerenciar-cadastro-refresh"
            onClick={recarregar}
            disabled={loading}
          >
            <FiRefreshCw />
            Atualizar
          </button>
        </section>

        <section className="gerenciar-cadastro-content">
          <div className="gerenciar-cadastro-content-header">
            <div>
              <span>Fila</span>
              <h2>Cadastros aguardando análise</h2>
            </div>
            <p>
              <FiClock />
              {cadastrosFiltrados.length} de {cadastros.length} exibidos ·{" "}
              {formatarAtualizacao(ultimaAtualizacao)}
            </p>
          </div>

          {loading ? (
            <div className="gerenciar-cadastro-state">
              <LoadingButton />
            </div>
          ) : erro ? (
            <div className="gerenciar-cadastro-state error">
              <strong>Não foi possível carregar os cadastros.</strong>
              <button type="button" onClick={recarregar}>
                <FiRefreshCw />
                Tentar novamente
              </button>
            </div>
          ) : cadastrosFiltrados.length === 0 ? (
            <div className="gerenciar-cadastro-state">
              <strong>Nenhum cadastro pendente no momento.</strong>
            </div>
          ) : (
            <>
              <div className="gerenciar-cadastro-table-wrap">
                <table className="gerenciar-cadastro-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Serviço</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cadastrosFiltrados.map((usuario) => (
                      <tr key={usuario.idCredenciais}>
                        <td>
                          <div className="gerenciar-cadastro-client">
                            <span>{obterIniciais(usuario.nome)}</span>
                            <strong>{usuario.nome || "Cliente sem nome"}</strong>
                          </div>
                        </td>
                        <td>
                          <span className={`gerenciar-cadastro-badge ${usuario.servico.toLowerCase()}`}>
                            {usuario.servico}
                          </span>
                        </td>
                        <td>
                          <span className="gerenciar-cadastro-status">Em análise</span>
                        </td>
                        <td>{renderizarAcoes(usuario)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="gerenciar-cadastro-mobile-list">
                {cadastrosFiltrados.map((usuario) => (
                  <article className="gerenciar-cadastro-mobile-card" key={usuario.idCredenciais}>
                    <div className="gerenciar-cadastro-mobile-card-header">
                      <span>{obterIniciais(usuario.nome)}</span>
                      <div>
                        <strong>{usuario.nome || "Cliente sem nome"}</strong>
                        <small>Cadastro pendente</small>
                      </div>
                    </div>
                    <div className="gerenciar-cadastro-mobile-card-footer">
                      <div>
                        <span className={`gerenciar-cadastro-badge ${usuario.servico.toLowerCase()}`}>
                          {usuario.servico}
                        </span>
                        <span className="gerenciar-cadastro-status">Em análise</span>
                      </div>
                      {renderizarAcoes(usuario)}
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      </main>

      {modalDadosPessoais && (
        <ModalDadosPessoaisCliente
          modal={modalDadosPessoais}
          setModal={() => setModalDadosPessoais(false)}
          idCliente={idCliente}
        />
      )}

      <Footer />
      {desconectar && <ModalDesconectar modal={desconectar} />}
    </>
  );
};

export default GerenciarCadastro;
